/**
 * The propers index — every day the chant book sets, searchable.
 *
 * THE SEARCH IS THE POINT. The book itself is answerable by day only if you
 * already know the page, and the closest thing to a by-day finder online is a
 * category listing with no search at all. "Proper 12", "Ad te", "Psalm 25" all
 * have to land somewhere.
 *
 * ⚠️ URL STATE IS APPLIED IN AN EFFECT, NEVER AS A `useState` INITIALIZER — the
 * rule `HymnBrowser` documents at length and `scripts/ci/prerender-hydration-
 * guard.mjs` enforces. One prerendered file serves every query string, so seeding
 * first render from `?q=` makes the client disagree with the server markup and
 * React throws the prerendered page away.
 */
import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { index } from '@/data/propers'
import { searchProperDays } from '@/lib/propers'
import { SectionTitle } from '@/components/HymnList'
import { PageHead } from '@/components/PageHead'

const BASE = '/hymnal/propers'

export default function PropersIndexPage() {
  const [params, setParams] = useSearchParams()
  const urlQuery = params.get('q') ?? ''

  // Starts at what SSR rendered — empty — and is filled a frame later. See header.
  const [query, setQuery] = useState('')
  useEffect(() => {
    setQuery(urlQuery)
  }, [urlQuery])

  const deferred = useDeferredValue(query)
  const results = useMemo(() => searchProperDays(index.days, deferred), [deferred])
  const searching = deferred.trim().length > 0

  const chantCount = index.days.reduce((n, d) => n + d.chants.length, 0)
  const withAlleluia = index.days.filter((d) => d.types.includes('alleluia')).length

  const description =
    `The introit, gradual psalm, alleluia verse, offertory and communion for ${index.days.length} days ` +
    `of the church year — ${chantCount} chants from ${index.sources.length} books, searchable by day, ` +
    'incipit, psalm or mode, each linking to the page it is printed on.'

  return (
    <article className="mx-auto max-w-3xl px-5 py-12">
      <PageHead
        title="Minor propers by day"
        description={description}
        path={BASE}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Minor propers by day',
          isPartOf: { '@type': 'WebSite', name: 'Fugue Lab' },
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: index.days.length,
            itemListElement: index.days.slice(0, 25).map((d, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: d.title,
              url: `https://fuguelab.com${BASE}/${d.slug}`,
            })),
          },
        }}
      />

      <header className="mb-8">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Reference</p>
        <h1 className="text-3xl sm:text-4xl font-display text-foreground mb-3">Minor propers by day</h1>
        <p className="text-muted-foreground max-w-2xl">
          The introit, gradual psalm, alleluia verse, offertory and communion for every Sunday and holy day. Pick the
          day and the page opens where the chant is printed. No single book sets all of these, so this draws on{' '}
          {index.sources.length}: {index.sources.map((s) => s.title).join(' and ')}. {withAlleluia} days carry an
          alleluia verse.
        </p>
      </header>

      <div className="mb-8">
        <label htmlFor="propers-search" className="sr-only">
          Search propers
        </label>
        <input
          id="propers-search"
          type="search"
          value={query}
          onChange={(e) => {
            const next = e.target.value
            setQuery(next)
            const p = new URLSearchParams(params)
            if (next) p.set('q', next)
            else p.delete('q')
            setParams(p, { replace: true })
          }}
          placeholder="Day, incipit, psalm — try “Proper 12”, “Ad te”, “Psalm 25”"
          className="w-full min-h-11 rounded border border-border bg-transparent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-border-strong focus:outline-none"
        />
      </div>

      {searching ? (
        <section>
          <SectionTitle>
            {results.length} {results.length === 1 ? 'day' : 'days'}
          </SectionTitle>
          {results.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing matches “{deferred}”.</p>
          ) : (
            <DayList days={results} />
          )}
        </section>
      ) : (
        index.groups.map((group) => (
          <section key={group.label} className="mb-8">
            <SectionTitle>{group.label}</SectionTitle>
            <DayList days={group.days} />
          </section>
        ))
      )}

      <SourceNote />
    </article>
  )
}

function DayList({ days }: { days: typeof index.days }) {
  return (
    <ul className="space-y-1.5">
      {days.map((d) => (
        <li key={d.slug} className="text-sm">
          <Link
            to={`${BASE}/${d.slug}`}
            className="text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground"
          >
            {d.title}
          </Link>{' '}
          <span className="text-xs text-muted-foreground">
            {d.chants.length} {d.chants.length === 1 ? 'chant' : 'chants'}
            {d.types.includes('alleluia') ? ' · alleluia verse' : ''}
            {d.yearVaries ? ' · varies by year' : ''}
          </span>
        </li>
      ))}
    </ul>
  )
}

/**
 * Attribution is a LICENCE CONDITION, not a courtesy: the book is CC BY-NC-ND, so
 * every page that surfaces its content carries the credit and the terms.
 */
export function SourceNote() {
  return (
    <footer className="mt-12 border-t border-border pt-5 text-xs text-muted-foreground space-y-3">
      {index.sources.map((source) => (
        <p key={source.url}>
          Chants indexed from{' '}
          <a
            href={source.url}
            className="underline decoration-border underline-offset-4 hover:text-foreground"
            rel="noopener noreferrer"
          >
            {source.title}
          </a>
          , adapted and edited by {source.adapter}.{' '}
          <a
            href={source.licenceUrl}
            className="underline decoration-border underline-offset-4 hover:text-foreground"
            rel="noopener noreferrer"
          >
            {source.licence}
          </a>
          . {source.note}
        </p>
      ))}
      <p>This index links to each original and reproduces none of the notation.</p>
    </footer>
  )
}
